import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { AutoResizeTextarea } from "@/components/ui/AutoResizeTextarea";
import { GdDocumentLangtextField } from "@/components/ui/GdRichTextarea";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  formatCurrency,
  formatQuantityInput,
  formatQuantityWithUnit,
  normalizeRabattAusweis,
  rabattIsActive,
  unitForCustomer,
} from "@/lib/documentCalc";
import { PositionAufschlagFields } from "@/pages/dokumente/PositionAufschlagFields";
import { PositionRabattFields } from "@/pages/dokumente/PositionRabattFields";
import { normalizeAufschlagModus } from "@/pages/dokumente/documentPricing";
import type { GdLohnart } from "@/lib/dokumente";
import type { GdPositionsart } from "@/pages/dokumente/gdPositionMeta";
import {
  GdPositionDocPriceCard,
  GdPositionDocSection,
  GdPositionInternalSection,
  GdPositionVisibilityLegend,
} from "@/pages/dokumente/DocumentPositionFieldSections";
import { DocumentPositionEditDialog } from "@/pages/dokumente/DocumentPositionEditDialog";
import { UnitAutocompleteField } from "@/pages/dokumente/UnitAutocompleteField";
import { KalkSumRow, KalkSumStack } from "@/pages/dokumente/KalkSumStack";
import { resolveLeistungVkNetto } from "@/pages/dokumente/documentLeistungPricing";
import {
  appendChildToMergedPosition,
  calcMergedAufschlagAmount,
  calcMergedChildrenSum,
  calcMergedTargetEp,
  createBlockPosition,
  getPositionMeta,
  mergedChildToEditorPosition,
  normalizePositionTyp,
  patchMergedPositionAufschlag,
  patchPositionMeta,
  POSITION_BLOCK_META,
  positionTotals,
  setMergedPositionEinzelpreis,
  syncLeistungEinzelpreis,
  updateMergedChildInPosition,
  type EditorPosition,
  type LeistungKalkulation,
} from "@/pages/dokumente/documentPositionModel";
import { DOCUMENT_EDITOR_NESTED_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";

interface DocumentMergedPositionEditDialogProps {
  open: boolean;
  position: EditorPosition | null;
  editable: boolean;
  currency: string;
  lohnarten: GdLohnart[];
  kundenId?: number | null;
  auftragId?: number | null;
  dokumentTyp?: string | null;
  onClose: () => void;
  onSave: (position: EditorPosition) => void;
  /** Entfernt eine Unterposition; liefert die aktualisierte Zusammenfassung oder null bei vollständiger Auflösung. */
  onExtractChild: (mergedPosition: EditorPosition, childIndex: number) => EditorPosition | null;
  zIndexClass?: string;
}

export function DocumentMergedPositionEditDialog({
  open,
  position,
  editable,
  currency,
  lohnarten,
  kundenId,
  auftragId,
  dokumentTyp = null,
  onClose,
  onSave,
  onExtractChild,
  zIndexClass,
}: DocumentMergedPositionEditDialogProps) {
  const [draft, setDraft] = useState<EditorPosition | null>(position);
  const [childEditIndex, setChildEditIndex] = useState<number | null>(null);
  const [pendingChild, setPendingChild] = useState<EditorPosition | null>(null);
  const openedLocalIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      openedLocalIdRef.current = null;
      setPendingChild(null);
      return;
    }
    const localId = position?.localId ?? null;
    if (localId !== openedLocalIdRef.current) {
      openedLocalIdRef.current = localId;
      setDraft(position);
      setChildEditIndex(null);
      setPendingChild(null);
    }
  }, [open, position]);

  const active = open ? draft ?? position : null;
  const positionMeta = active ? getPositionMeta(active) : {};
  const mergedChildren = positionMeta.merged_children ?? [];
  const showPositionsartToggle = dokumentTyp === "angebot";
  const positionsartValue: "standard" | GdPositionsart =
    positionMeta.positionsart === "optional" || positionMeta.positionsart === "alternativ"
      ? positionMeta.positionsart
      : "standard";

  const preview = useMemo(() => {
    if (!active) return null;
    return positionTotals(syncLeistungEinzelpreis(active));
  }, [active]);

  const childrenSum = useMemo(
    () => calcMergedChildrenSum(mergedChildren),
    [mergedChildren],
  );

  const leistungKalk = (active?.kalkulation_json || {
    artikel_liste: [],
    lohn_liste: [],
  }) as LeistungKalkulation;

  const leistungVkNetto = active
    ? resolveLeistungVkNetto(leistungKalk, active.einzelpreis_netto, active.menge)
    : 0;
  const aufschlagModus = normalizeAufschlagModus(positionMeta.merged_aufschlag_modus);
  const aufschlagBetrag = calcMergedAufschlagAmount(childrenSum, positionMeta);
  const targetEp = calcMergedTargetEp(childrenSum, positionMeta);
  const aufschlagAktiv = Math.abs(aufschlagBetrag) >= 0.005;
  const epManuellAktiv = Math.abs(leistungVkNetto - targetEp) >= 0.005;
  const epDiff = leistungVkNetto - targetEp;
  const leistungPositionNetto = preview?.zeilen_netto ?? 0;
  const leistungEpImDokument = active
    ? unitForCustomer(leistungVkNetto, Number(active.menge ?? 1), active)
    : 0;
  const rabattSichtbar = Boolean(
    active && normalizeRabattAusweis(active.rabatt_ausweis) === "dokument" && rabattIsActive(active),
  );

  const childEditPosition = pendingChild
    ? pendingChild
    : childEditIndex != null && mergedChildren[childEditIndex]
      ? mergedChildToEditorPosition(mergedChildren[childEditIndex])
      : null;

  function patch(partial: Partial<EditorPosition>) {
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  function patchMeta(partial: Parameters<typeof patchPositionMeta>[1]) {
    setDraft((prev) => (prev ? patchPositionMeta(prev, partial) : prev));
  }

  function patchEpManuell(value: number) {
    setDraft((prev) => (prev ? setMergedPositionEinzelpreis(prev, value) : prev));
  }

  function resetEpToTarget() {
    patchEpManuell(targetEp);
  }

  function patchAufschlag(
    partial: Parameters<typeof patchMergedPositionAufschlag>[1],
  ) {
    setDraft((prev) => (prev ? patchMergedPositionAufschlag(prev, partial) : prev));
  }

  function startNewChild(typ: "artikel" | "leistung") {
    setChildEditIndex(null);
    setPendingChild(
      createBlockPosition(typ, mergedChildren.length, {
        lohnarten: typ === "leistung" ? lohnarten : undefined,
      }),
    );
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) setDraft(position);
    else onClose();
  }

  function handleSave() {
    if (!draft || !editable) return;
    onSave(syncLeistungEinzelpreis(draft));
    onClose();
  }

  function handleChildSave(updatedChild: EditorPosition) {
    if (!draft || !editable) return;
    if (pendingChild) {
      setDraft(appendChildToMergedPosition(draft, updatedChild));
      setPendingChild(null);
      return;
    }
    if (childEditIndex == null) return;
    setDraft(updateMergedChildInPosition(draft, childEditIndex, updatedChild));
    setChildEditIndex(null);
  }

  function closeChildDialog() {
    setChildEditIndex(null);
    setPendingChild(null);
  }

  function handleExtractChild(childIndex: number) {
    if (!draft || !editable) return;
    if (childEditIndex === childIndex) setChildEditIndex(null);
    else if (childEditIndex != null && childEditIndex > childIndex) {
      setChildEditIndex(childEditIndex - 1);
    }
    const updated = onExtractChild(syncLeistungEinzelpreis(draft), childIndex);
    if (!updated) {
      onClose();
      return;
    }
    setDraft(updated);
  }

  if (!active) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => handleOpenChange(false)}
        title={
          editable ? "Zusammengefasste Position bearbeiten" : "Zusammengefasste Position ansehen"
        }
        size="4xl"
        fullScreenMobile
        zIndexClass={zIndexClass}
        dirty={editable}
        footer={
          editable ? (
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="button" onClick={handleSave}>
                Übernehmen
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
                Schließen
              </Button>
            </div>
          )
        }
      >
        <div className="space-y-5">
          <GdPositionVisibilityLegend />

          {showPositionsartToggle ? (
            <div className="space-y-1">
              <span className="text-sm text-label-secondary">Positionsart</span>
              <SegmentedControl
                value={positionsartValue}
                onChange={(value) => {
                  const next = value as "standard" | GdPositionsart;
                  patchMeta({
                    positionsart: next === "standard" ? undefined : next,
                  });
                }}
                disabled={!editable}
                options={[
                  { value: "standard", label: "Standard" },
                  { value: "optional", label: "Optional" },
                  { value: "alternativ", label: "Alternativ" },
                ]}
              />
              <p className="text-xs text-label-tertiary">
                Optional zählt zur Angebotssumme, Alternativ nicht. Bei der AB wählen Sie die
                Übernahme.
              </p>
            </div>
          ) : null}

          <GdPositionDocSection hint="Bezeichnung, Menge und Langtext erscheinen in der Positionsliste als eine Leistungszeile.">
            <div className="grid gap-3 sm:grid-cols-[minmax(4.5rem,5.5rem)_minmax(6.5rem,8rem)_1fr] sm:items-end">
              <TextInput
                label="Menge"
                type="number"
                className="gd-pos-menge-field"
                value={formatQuantityInput(active.menge)}
                onChange={(e) => patch({ menge: Number(e.target.value) })}
                disabled={!editable}
              />
              <label className="block space-y-1 text-sm">
                <span className="text-label-secondary">Einheit</span>
                <UnitAutocompleteField
                  value={active.einheit || ""}
                  onChange={(value) => patch({ einheit: value })}
                  disabled={!editable}
                />
              </label>
              <TextInput
                label="Leistungsbezeichnung"
                value={active.bezeichnung}
                onChange={(e) => patch({ bezeichnung: e.target.value })}
                disabled={!editable}
              />
            </div>
            <GdDocumentLangtextField
              label="Beschreibung / Langtext"
              value={active.beschreibung || ""}
              onChange={(value) => patch({ beschreibung: value })}
              disabled={!editable}
            />
            <GdPositionDocPriceCard>
              <div className="gd-pos-doc-price__ep">
                <span className="gd-pos-doc-price__ep-label">EP netto im Dokument</span>
                <span className="gd-pos-doc-price__ep-value">
                  {formatCurrency(leistungEpImDokument, currency)}
                </span>
                <span className="gd-pos-doc-price__ep-hint">
                  {rabattIsActive(active)
                    ? rabattSichtbar
                      ? "Rabatt wird auf der Rechnung ausgewiesen"
                      : `${formatCurrency(leistungVkNetto, currency)} vor Rabatt`
                    : epManuellAktiv
                      ? "Manuell überschrieben"
                      : aufschlagAktiv
                        ? "Summe der Unterpositionen zzgl. internem Aufschlag"
                        : "Summe der Unterpositionen"}
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 sm:items-start">
                <PositionRabattFields
                  value={active}
                  disabled={!editable}
                  onChange={(partial) => patch(partial)}
                />
                <TextInput
                  label="MwSt %"
                  type="number"
                  value={String(active.steuersatz_prozent)}
                  onChange={(e) => patch({ steuersatz_prozent: Number(e.target.value) })}
                  disabled={!editable}
                />
              </div>
              <div className="gd-pos-doc-price__totals">
                <div className="gd-pos-doc-price__total-row">
                  <span>
                    Position netto (× {formatQuantityInput(active.menge)} {active.einheit || "Stk"})
                  </span>
                  <span>{formatCurrency(leistungPositionNetto, currency)}</span>
                </div>
                {preview ? (
                  <div className="gd-pos-doc-price__total-row gd-pos-doc-price__total-row--grand">
                    <span>Position brutto</span>
                    <span>{formatCurrency(preview.zeilen_brutto, currency)}</span>
                  </div>
                ) : null}
              </div>
            </GdPositionDocPriceCard>
          </GdPositionDocSection>

          <GdPositionInternalSection
            title="Unterpositionen"
            hint={
              editable
                ? "Artikel oder Leistung direkt als Unterposition anlegen. Klicken zum Bearbeiten. Mit „Herauslösen“ erscheint die Position wieder einzeln in der Liste."
                : "Klicken zum Ansehen der Artikeldetails und Kalkulation."
            }
          >
            {editable ? (
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => startNewChild("artikel")}>
                  Artikel hinzufügen
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => startNewChild("leistung")}>
                  Leistung hinzufügen
                </Button>
              </div>
            ) : null}
            {mergedChildren.length === 0 ? (
              <p className="text-sm text-label-secondary">Keine Unterpositionen vorhanden.</p>
            ) : (
              <ul className="gd-merged-child-list">
                {mergedChildren.map((child, index) => {
                  const row = mergedChildToEditorPosition(child);
                  const typ = normalizePositionTyp(child.position_typ);
                  const meta = POSITION_BLOCK_META[typ as keyof typeof POSITION_BLOCK_META];
                  const synced =
                    typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
                  const totals = positionTotals(synced);
                  const mengeLabel = formatQuantityWithUnit(synced.menge, synced.einheit);

                  return (
                    <li key={index} className="gd-merged-child-row">
                      <button
                        type="button"
                        className="gd-merged-child-item"
                        onClick={() => {
                          setPendingChild(null);
                          setChildEditIndex(index);
                        }}
                      >
                        <span className="gd-merged-child-item__icon" aria-hidden>
                          {meta?.icon ?? "•"}
                        </span>
                        <span className="gd-merged-child-item__body">
                          <span className="gd-merged-child-item__type">
                            {meta?.label ?? typ}
                          </span>
                          <span className="gd-merged-child-item__title">
                            {child.bezeichnung || "Ohne Bezeichnung"}
                          </span>
                          <span className="gd-merged-child-item__meta">{mengeLabel}</span>
                        </span>
                        <span className="gd-merged-child-item__sum">
                          {formatCurrency(totals.zeilen_netto, currency)}
                        </span>
                        <span className="gd-merged-child-item__chevron" aria-hidden>
                          ›
                        </span>
                      </button>
                      {editable ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="gd-merged-child-extract"
                          title="Als eigene Position wiederherstellen"
                          onClick={() => handleExtractChild(index)}
                        >
                          Herauslösen
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </GdPositionInternalSection>

          <GdPositionInternalSection
            title="Preiskalkulation"
            hint="Die Summe der Unterpositionen bildet die Basis. Ein interner Aufschlag erhöht den EP im Dokument. Den EP können Sie zusätzlich manuell überschreiben."
          >
            <div className="gd-leistung-pricing-grid">
              <div className="gd-leistung-pricing__controls">
                <p className="gd-leistung-pricing__panel-title">Steuerung</p>
                <PositionAufschlagFields
                  label={aufschlagModus === "betrag" ? "Interner Aufschlag €" : "Interner Aufschlag %"}
                  hint={
                    aufschlagModus === "betrag"
                      ? "Eurobetrag auf die Summe der Unterpositionen. Erscheint nicht im Kundendokument."
                      : "Prozent auf die Summe der Unterpositionen. Erscheint nicht im Kundendokument."
                  }
                  value={{
                    aufschlag_prozent: positionMeta.merged_aufschlag_prozent,
                    aufschlag_betrag: positionMeta.merged_aufschlag_betrag,
                    aufschlag_modus: positionMeta.merged_aufschlag_modus,
                  }}
                  disabled={!editable}
                  onValueChange={(amount) =>
                    patchAufschlag(
                      aufschlagModus === "betrag"
                        ? { merged_aufschlag_betrag: amount, merged_aufschlag_modus: "betrag" }
                        : { merged_aufschlag_prozent: amount, merged_aufschlag_modus: "prozent" },
                    )
                  }
                  onModusChange={(modus) => patchAufschlag({ merged_aufschlag_modus: modus })}
                />
                <TextInput
                  label="EP netto im Dokument"
                  type="number"
                  value={formatQuantityInput(leistungVkNetto)}
                  onChange={(e) => patchEpManuell(Number(e.target.value))}
                  disabled={!editable}
                  hint="Überschreibt Summe und internen Aufschlag"
                />
                {editable && epManuellAktiv ? (
                  <Button type="button" size="sm" variant="ghost" onClick={resetEpToTarget}>
                    Auf kalkulierten EP zurücksetzen
                  </Button>
                ) : null}
                <p className="gd-leistung-pricing__controls-hint">
                  Änderungen an Unterpositionen oder am Aufschlag aktualisieren den Gesamtbetrag
                  automatisch, solange kein manueller EP gesetzt ist.
                </p>
              </div>
              <div className="gd-leistung-pricing__breakdown">
                <p className="gd-leistung-pricing__panel-title">Kalkulationskette</p>
                <KalkSumStack className="gd-leistung-pricing__stack">
                  <KalkSumRow
                    label="Summe Unterpositionen netto"
                    value={childrenSum}
                    currency={currency}
                  />
                  {aufschlagAktiv ? (
                    <KalkSumRow
                      label={
                        aufschlagModus === "betrag"
                          ? "Interner Aufschlag"
                          : `Interner Aufschlag ${positionMeta.merged_aufschlag_prozent} %`
                      }
                      value={aufschlagBetrag}
                      currency={currency}
                    />
                  ) : null}
                  {aufschlagAktiv ? (
                    <KalkSumRow
                      label="Kalkulierter EP netto"
                      value={targetEp}
                      currency={currency}
                      bold
                      separator
                    />
                  ) : null}
                  {epManuellAktiv ? (
                    <KalkSumRow
                      label="Abweichung (manueller EP)"
                      value={epDiff}
                      currency={currency}
                      accent
                    />
                  ) : null}
                  <KalkSumRow
                    label="Gültiger EP netto"
                    value={leistungVkNetto}
                    currency={currency}
                    bold
                    separator={!aufschlagAktiv}
                  />
                  <KalkSumRow
                    label={`Position netto (× ${formatQuantityInput(active.menge)} ${active.einheit || "Stk"})`}
                    value={leistungPositionNetto}
                    currency={currency}
                    bold
                  />
                </KalkSumStack>
              </div>
            </div>
          </GdPositionInternalSection>

          <GdPositionInternalSection
            title="Interne Notiz"
            hint="Nur für das Team sichtbar — erscheint nicht im Kundendokument."
          >
            <AutoResizeTextarea
              value={positionMeta.interne_notiz || ""}
              onChange={(e) => patchMeta({ interne_notiz: e.target.value })}
              disabled={!editable}
              minRows={2}
              placeholder="Notizen zur Nachkalkulation, interne Hinweise …"
            />
          </GdPositionInternalSection>
        </div>
      </Dialog>

      <DocumentPositionEditDialog
        open={childEditIndex != null || pendingChild != null}
        position={childEditPosition}
        editable={editable}
        currency={currency}
        lohnarten={lohnarten}
        kundenId={kundenId}
        auftragId={auftragId}
        dokumentTyp={dokumentTyp}
        onClose={closeChildDialog}
        zIndexClass={DOCUMENT_EDITOR_NESTED_OVERLAY_Z}
        onSave={handleChildSave}
      />
    </>
  );
}
